import axios from "axios";
import { getWorkflowProductDeclaration } from "src/views/products/workflow/services/WorkflowProductService";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { nuclearn_api_url } from "src/App";
import { PersonaConfigurationService } from "../services/PersonaConfigurationService";
import { useChatStore } from "../stores/ChatStore";
import { FIFTY_FIFTY_NINE_PRODUCT } from "src/views/products/fifty-fifty-nine/fiftyFiftyNineProduct";

/**
 * Start a 50.59 Evaluation Lead conversation seeded to resume a record.
 *
 * The lead is resolved through the workflow declaration endpoint (admin
 * binding -> resource slot -> seeded default), not a hardcoded slug, and the
 * dialogue is created with the 50.59 product slug — the product value is what
 * activates the workflow delegation tools and record context injection
 * server-side (a dialogue stamped "atom-assist" gets neither).
 *
 * Finalization stays gated (complete criteria, passing challenge, authored
 * report, human approval) — this just sends the agent to finish the work,
 * it never stamps a record finalized.
 */
export const useResumeRecordWithAgent = () => {
  const [resumingRecordId, setResumingRecordId] = useState<number | null>(null);
  const history = useHistory();
  const { setPersona, setConfig, setInitialUserMessage } = useChatStore();

  const resumeRecord = useCallback(
    async (recordId: number) => {
      setResumingRecordId(recordId);
      try {
        // 1. Resolve which persona currently fills the lead slot
        const declaration = await getWorkflowProductDeclaration(
          FIFTY_FIFTY_NINE_PRODUCT.productKey,
        );
        const leadSlot = declaration.persona_slots?.find(
          (slot) => slot.slot_key === FIFTY_FIFTY_NINE_PRODUCT.leadSlotKey,
        );
        const leadSlug: string | undefined = leadSlot?.resolved_persona?.slug;
        if (!leadSlug) {
          throw new Error("No persona is configured for the 50.59 lead slot");
        }
        const personaResponse = await axios.get(
          `${nuclearn_api_url}/chat/config/${leadSlug}`,
        );
        const persona = personaResponse.data;
        const chatConfig =
          PersonaConfigurationService.buildChatConfigFromPersona(persona);

        // 2. Create the dialogue bound to the lead, under the 50.59 product
        const createResponse = await axios.post(
          `${nuclearn_api_url}/chat/create-dialogue`,
          {
            model_id: chatConfig.modelId ?? null,
            model_version_number: chatConfig.modelVersionNum ?? null,
            search_datasets: (chatConfig.datasets ?? []).map(
              (d: { id: number }) => d.id,
            ),
            n_search_results: chatConfig.topSearchResults,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
              text_gen_model_params: {},
            },
            tool_calling: true,
            chat_config_id: chatConfig.chatConfigId,
            product: FIFTY_FIFTY_NINE_PRODUCT.productKey,
          },
        );
        const dialogueId: number = createResponse.data;

        // 3. Navigate to the 50.59 chat with the resume instruction queued
        setConfig(chatConfig);
        setPersona(PersonaConfigurationService.buildPersonaDataFromAPI(persona));
        setInitialUserMessage(
          `Resume 50.59 record ${recordId}. Fetch it with ` +
            `'Get 50.59 Records', summarize where it stands and what is ` +
            `missing, then drive it to finalization: complete any missing ` +
            `research or criteria, run the independent challenge loop ` +
            `until it passes, author the report, and request finalization.`,
        );
        history.push(
          `${FIFTY_FIFTY_NINE_PRODUCT.basePath}/chat/${dialogueId}`,
        );
        return true;
      } catch (error) {
        console.error("Failed to resume record with agent:", error);
        return false;
      } finally {
        setResumingRecordId(null);
      }
    },
    [history, setPersona, setConfig, setInitialUserMessage],
  );

  return { resumeRecord, resumingRecordId };
};
